import type { UID } from 'agora-rtc-sdk-ng'

import type {
  Api_Image_Overlay_Anchor,
  Api_Interaction,
  Api_Product,
  Api_Trivia_Giveaway_Question,
  Api_Video_LiveStreamSubtitles,
} from '@core/api'

export enum Enum_LiveStream_Provider {
  AGORA = 'agora',
  IVS = 'ivs',
  CDN = 'cdn',
}

export enum Enum_LiveStream_Status {
  IDLE = 'idle',
  ACTIVE = 'active',
  PAUSED = 'paused',
  REPLAY = 'replay',
  COMPLETED = 'completed',
  EXPIRED = 'expired',
  DISABLED = 'disabled',
  ERRORED = 'errored',
}

export enum Enum_LiveStream_User_type {
  VIEWER = 'viewer',
  HOST = 'host',
  MODERATOR = 'moderator',
  BOT = 'bot',
  AI_ASSISTANT = 'ai_assistant',
}

export enum Enum_LiveStream_Private_Messaging {
  ALWAYS_ON = 'always_on',
  REPLAY_ONLY = 'replay_only',
  DISABLED = 'disabled',
}

export type Api_Livestream_Entity_Type =
  | 'product'
  | 'poll'
  | 'question'
  | 'giveaway'
  | 'giveaway_collection'
  | 'link'
  | 'trivia_giveaway'
  | 'trivia_giveaway_questions'
  | 'trivia_giveaway_answers'
  | 'comment_giveaway'
  | 'image_overlay'
export enum Enum_LiveStream_Auto_Response {
  ALWAYS_ON = 'always_on',
  MODERATED = 'moderated',
  DISABLED = 'disabled',
}

export type Api_LiveStream_Winner_Detail = {
  username: string
  guest_id: string
}

/**
 * `image_overlay` specific: the full render state of the highlighted image,
 * carried on both the highlight payload and the state snapshot so a replay can
 * reconstruct exactly what was on screen at that moment, even if the
 * interaction has been edited or removed since.
 *
 * Everything is optional: older payloads and snapshots carry only
 * `image_overlay_id`, and the image may arrive as a storage `image_key` rather
 * than a `image_url`, so the interaction's own `image_overlays` record stays
 * the fallback source. See `resolveImageOverlay`.
 */
export interface Api_LiveStream_Image_Overlay_State {
  /** which image in the group is highlighted */
  image_overlay_id?: string
  image_url?: string
  /** storage key of the image — not a URL, so it cannot be rendered directly */
  image_key?: string
  anchor?: Api_Image_Overlay_Anchor
  offset_x?: number
  offset_y?: number
  width?: number
  height?: number
  z_index?: number
}

export interface Api_LiveStream_Entity extends Api_LiveStream_Image_Overlay_State {
  entity_type?: Api_Livestream_Entity_Type
  entity_id?: string
  action_type?: Api_LiveStream_Entity_Action['action_type'] // present when stored from an unhighlight payload
  tally?: Record<'total' | string, number>
  winners?: string[]
  winners_details?: Api_LiveStream_Winner_Detail[]
  won?: boolean // comment giveaway specific field
  interactive_url?: string | null // comment giveaway specific field; null on losers
}

export interface Api_LiveStream_Entity_Action extends Api_LiveStream_Image_Overlay_State {
  action_type?:
    | 'highlight_entity'
    | 'unhighlight_entity'
    | 'disable_entity'
    | 'enable_entity'
    | 'giveaway_collection'
  entity_type?: Api_Livestream_Entity_Type
  entity_id?: string
  entities?: Api_LiveStream_Entity[]
  message_id?: string
  text?: string
  username?: string
  tally?: Record<'total' | string, number>
  winners?: string[]
  winners_details?: Api_LiveStream_Winner_Detail[]
  option_sets?: Api_Trivia_Giveaway_Question[]
}

export interface Api_LiveStream_Message {
  event_type:
    | 'message'
    | 'private_message'
    | 'join'
    | 'leave'
    | 'heart'
    | 'pin_message'
    | 'idle_message'
  id: string
  inserted_at?: string
  is_nsfw?: boolean
  text?: string
  original_text?: string
  user_type: Enum_LiveStream_User_type
  username?: string
  elapsed_time?: number
  reply?: {
    message_id: string
    text: string
    username: string
  } | null
  visibility?: 'public' | 'private'
}

/**
 * Chat messages received from API (not WS)
 */
export interface Api_LiveStream_Chat_Message {
  elapsed_time: number
  guest_id: string
  id: string
  inserted_at: string
  is_nsfw?: boolean
  original_text: string
  replay_id: number
  reply: {
    message_id: string
    text: string
    username: string
  } | null
  status: string // "approved"
  text: string
  user_id: number | null
  user_type: Enum_LiveStream_User_type
  username: string
  visibility?: 'public' | 'private'
}

export interface Api_LiveStream_PinnedMessage {
  live_stream_id: string
  message_id: string
  text: string
  username: string
}

export interface Api_LiveStream_Chat_Message_Collection {
  entries: Api_LiveStream_Chat_Message[]
  next_page: string | null
}

export interface Api_LiveStream_Event_Collection {
  entries: Api_LiveStream_API_Event[]
  next_page: string | null
}

export interface Api_LiveStream_State_Snapshot {
  elapsed_time?: number
  replay_id?: string
  state_snapshot?: string
  version?: number
}

export interface Api_LiveStream_API_Event {
  action_id: string
  action_type: string
  elapsed_time: number
  guest_id: string | null
  live_stream_id?: number
  payload: Api_LiveStream_Entity_Action | null
  user_id: number | null
  user_type: Enum_LiveStream_User_type
  username?: string
  replay_id: number
}

export interface Api_LiveStream_Event {
  event_type: string
  id: string
  payload: Api_LiveStream_Entity_Action | null
  user_type: Enum_LiveStream_User_type
  elapsed_time: number
}

export interface Api_LiveStream_Running_Announcement {
  event_type: 'update_announcement'
  live_stream_id: string
  announcement: string
}

export interface AgoraProviderInfo {
  // Representing the order of the streamers in the live stream.
  active_streamers_uids?: UID[]

  active_streamers?: Array<{
    parent_uid?: UID
    uid: UID
    username: string
    visitor_id?: string
  }>

  // Subscriber Agora token.
  token?: string

  // Agora UID is shared by viewer and guest speaker roles.
  uid: UID
}

export interface Api_LiveStream_Create_Interaction {
  event_type: 'create_interaction'
  id: string
  interaction: Api_Interaction
  live_stream_id: string
}

export interface Api_LiveStream_Flash_Deal_Listing {
  coupon_code: string
  deal_listing_id: string
  discount_amount: string
  discount_type: 'percentage' | 'fixed_amount'
  ends_at?: string
  product_ext_id: string
  product_id: string
  product_unit_id?: string
  starts_at?: string
}

export type Api_Video_Promotion_Type = 'product' | 'generic' | 'announcement'

export type Api_Video_Promotion_Offer_Type =
  'percentage_discount' | 'fixed_amount_discount' | 'free_gift' | 'bundle'

/**
 * Unused fields come back as `null` rather than being omitted, so every key is
 * always present. `display_text` and the action fields are announcement-only in
 * the deployed API; the backend ignores them on the other types.
 */
export interface Api_Video_Promotion_Payload {
  offer_type: Api_Video_Promotion_Offer_Type | null
  amount: number | null
  offer_description: string | null
  code: string | null
  display_text: string | null
  action_type: string | null
  action_url: string | null
}

export interface Api_Video_Promotion {
  id: string
  video_id: string
  /** Only set for `product` promotions, and matches `Api_Product['id']`. */
  product_id: string | null
  type: Api_Video_Promotion_Type
  starts_at: string | null
  ends_at: string | null
  payload: Api_Video_Promotion_Payload
  /**
   * Calculated when the server serializes, so it is already stale on arrival
   * and nothing is sent when a start or end time passes. Derive the status
   * from starts_at/ends_at instead of reading this.
   */
  status: 'active' | 'scheduled' | 'expired'
  inserted_at: string
  updated_at: string
}

/**
 * Sent on create, update and delete during an active or paused livestream. The
 * list is complete and unpaginated, so it replaces the local one; an empty
 * array means the last promotion is gone, not that nothing changed.
 */
export interface Api_LiveStream_Update_Video_Promotions {
  event_type: 'update_video_promotions'
  live_stream_id: string
  video_promotions: Api_Video_Promotion[]
}

export interface Api_LiveStream {
  announcement?: string
  blocked_viewers?: string[]
  business_privacy_policy_url?: string
  chat_enabled: boolean
  chat_in_replay_enabled: boolean
  chat_moderation_enabled: boolean
  countdown_started_at?: string
  deals: Api_LiveStream_Flash_Deal_Listing[]
  disabled_entities: Api_LiveStream_Entity[]
  ended_at: string
  ended_one_time_entities?: Api_LiveStream_Entity[]
  ended_polls?: string[]
  event_name?: string
  highlighted_entities?: Api_LiveStream_Entity[]
  highlighted_entity: Api_LiveStream_Entity | null
  host_username?: string | null
  insights?: {
    // viewers_count is the accumulated total from the detail API.
    // current_viewer_count is the concurrent count on that same response.
    hearts_count: number
    viewers_count: number
    current_viewer_count?: number
  }
  interactions: Api_Interaction[]
  open_entry_giveaway?: string
  pinned_message?: Api_LiveStream_PinnedMessage | null
  pinned_messages?: Api_LiveStream_PinnedMessage[]
  playback_url: string
  private_messaging?: Enum_LiveStream_Private_Messaging
  products: Api_Product[]
  provider: Enum_LiveStream_Provider
  provider_info?: AgoraProviderInfo
  reconnect_grace_period?: number
  replay_enabled: boolean
  replay_filtered_url?: string | null
  replay_messaging_enabled: boolean
  replay_url: string | null
  scheduled_at: string
  started_at?: string | null
  status: Enum_LiveStream_Status
  scheduled_end_at?: string
  test_mode?: boolean
  terms_and_conditions?: string
  username?: string // websocket only
  username_info?: {
    is_hydrated: boolean
    username: string
  } // websocket only
  user_type?: string // websocket only
  video_id: string
  video_promotions?: Api_Video_Promotion[]
  viewers_count?: number
  viewers_count_enabled: boolean
  hearts_count?: number
  hearts_count_enabled?: boolean
  web_share_url: string
  replay?: Api_LiveStream_Replay
  subtitles?: (Api_Video_LiveStreamSubtitles | Api_LiveStream_Subtitles)[]
}

export interface Api_LiveStream_Replay {
  download_url: string | null
  id: string
  inserted_at: string
  is_downloadable: boolean
  is_primary: boolean
  live_stream_id: string
  name: string | null
  skips_to: number | null
  started_at: string | null
  stops_at?: number | null
  transcoding_status: string | null
  updated_at: string
  url: string
}

export interface Api_LiveStream_Message_Rate_Limit {
  limit: number
  milliseconds: number
}

// Used for Livestream Transcript provided to viewer via websocket
export interface Api_LiveStream_Subtitles {
  duration_ms: number
  event_type: string
  live_stream_id: string
  start_pts_ms: number
  text: string
}
